import { useMemo, useState, type ReactNode } from 'react'
import { ChevronLeft } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useClasses,
  useDepartments,
  useHierarchyLevel,
  useSubclasses,
} from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildPoLevelDetailRows,
  DC_OPTIONS,
  ESTIMATED_ARR_WK_OPTIONS,
  EVENT_OPTIONS,
  ORIG_ARR_WK_OPTIONS,
  PO_OPTIONS,
  SNAPSHOT_DATE_OPTIONS,
  type PoLevelDetailFilterState,
} from './poLevelDetailViewMockData'
import { POLevelDetailViewTable } from './POLevelDetailViewTable'

const ALL = '__all__'

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface IdOption {
  id: string
  label: string
}

function IdSelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: IdOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const DESCRIPTION =
  'Detailed view of each PO showing sellable units, dollars, department hierarchy, and how many days and dollars shifted to help pinpoint line-level timing and cost changes.'

function includeSelectedOption(
  options: string[],
  selected: string | null
): string[] {
  if (!selected || options.includes(selected)) return options
  return [...options, selected].sort()
}

interface Props {
  initialFilters?: Partial<PoLevelDetailFilterState>
  isDrillMode?: boolean
  onBack?: () => void
}

export function POLevelDetailViewMockup({
  initialFilters,
  isDrillMode,
  onBack,
}: Props) {
  const [snapshotDate, setSnapshotDate] = useState<string>(
    initialFilters?.snapshotDate ?? 'Latest'
  )
  const [season, setSeason] = useState<string | null>(
    initialFilters?.season ?? null
  )
  const [event, setEvent] = useState<string | null>(
    initialFilters?.event ?? null
  )
  const [divisionId, setDivisionId] = useState<string | null>(
    initialFilters?.divisionId ?? null
  )
  const [departmentId, setDepartmentId] = useState<string | null>(
    initialFilters?.departmentId ?? null
  )
  const [classId, setClassId] = useState<string | null>(
    initialFilters?.classId ?? null
  )
  const [subclassId, setSubclassId] = useState<string | null>(
    initialFilters?.subclassId ?? null
  )
  const [dc, setDc] = useState<string | null>(initialFilters?.dc ?? null)
  const [origArrWk, setOrigArrWk] = useState<string | null>(
    initialFilters?.origArrWk ?? null
  )
  const [estimatedArrWk, setEstimatedArrWk] = useState<string | null>(
    initialFilters?.estimatedArrWk ?? null
  )
  const [po, setPo] = useState<string | null>(null)

  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const { data: divisions, isLoading: divisionsLoading } =
    useHierarchyLevel('DIVISION')
  const { data: departmentsScoped, isLoading: departmentsScopedLoading } =
    useHierarchyLevel('DEPARTMENT', divisionId)
  const { data: departmentsAll, isLoading: departmentsAllLoading } =
    useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)

  const departments = divisionId ? departmentsScoped : departmentsAll
  const departmentsLoading = divisionId
    ? departmentsScopedLoading
    : departmentsAllLoading

  const seasonOptions = (seasons ?? []).map(s => s.name)
  const divisionOptions: IdOption[] = (divisions ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const departmentOptions: IdOption[] = (departments ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const classOptions: IdOption[] = (classes ?? []).map(c => ({
    id: c.id,
    label: `${c.code} - ${c.name}`,
  }))
  const subclassOptions: IdOption[] = (subclasses ?? []).map(s => ({
    id: s.id,
    label: `${s.code} - ${s.name}`,
  }))

  const filters = useMemo<PoLevelDetailFilterState>(
    () => ({
      snapshotDate,
      season,
      event,
      divisionId,
      departmentId,
      classId,
      subclassId,
      dc,
      origArrWk,
      estimatedArrWk,
      po,
    }),
    [
      snapshotDate,
      season,
      event,
      divisionId,
      departmentId,
      classId,
      subclassId,
      dc,
      origArrWk,
      estimatedArrWk,
      po,
    ]
  )

  const rows = useMemo(
    () => buildPoLevelDetailRows(filters, { ensureDrillResults: isDrillMode }),
    [filters, isDrillMode]
  )
  const origArrWkOptions = useMemo(
    () => includeSelectedOption(ORIG_ARR_WK_OPTIONS, origArrWk),
    [origArrWk]
  )
  const estimatedArrWkOptions = useMemo(
    () => includeSelectedOption(ESTIMATED_ARR_WK_OPTIONS, estimatedArrWk),
    [estimatedArrWk]
  )
  const poOptions = useMemo(
    () =>
      Array.from(new Set([...PO_OPTIONS, ...rows.map(row => row.po)])).sort(),
    [rows]
  )

  const handleDivisionChange = (v: string | null) => {
    setDivisionId(v)
    setDepartmentId(null)
    setClassId(null)
    setSubclassId(null)
  }

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {isDrillMode && onBack && (
        <button
          onClick={onBack}
          className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ChevronLeft className="h-3 w-3" />
          Back to Cost Shift Grid
        </button>
      )}
      {/* Top strip */}
      <p className="max-w-2xl text-[11px] leading-tight text-muted-foreground">
        {DESCRIPTION}
      </p>

      {/* Filter grid — 7 columns, row 2 (4 filters) wraps under columns 1-4 */}
      <div className="grid grid-cols-7 gap-4">
        <FilterField label="Snapshot Date">
          <Select value={snapshotDate} onValueChange={setSnapshotDate}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SNAPSHOT_DATE_OPTIONS.map(o => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Season">
          <SimpleSelect
            value={season}
            onValueChange={setSeason}
            options={seasonOptions}
            loading={seasonsLoading}
          />
        </FilterField>
        <FilterField label="Event">
          <SimpleSelect
            value={event}
            onValueChange={setEvent}
            options={EVENT_OPTIONS}
          />
        </FilterField>
        <FilterField label="Division">
          <IdSelect
            value={divisionId}
            onValueChange={handleDivisionChange}
            options={divisionOptions}
            loading={divisionsLoading}
          />
        </FilterField>
        <FilterField label="Department">
          <IdSelect
            value={departmentId}
            onValueChange={handleDepartmentChange}
            options={departmentOptions}
            loading={departmentsLoading}
          />
        </FilterField>
        <FilterField label="Class">
          <IdSelect
            value={classId}
            onValueChange={handleClassChange}
            options={classOptions}
            loading={classesLoading}
            disabled={!departmentId}
          />
        </FilterField>
        <FilterField label="SubClass">
          <IdSelect
            value={subclassId}
            onValueChange={setSubclassId}
            options={subclassOptions}
            loading={subclassesLoading}
            disabled={!classId}
          />
        </FilterField>

        <FilterField label="DC">
          <SimpleSelect
            value={dc}
            onValueChange={setDc}
            options={DC_OPTIONS}
            formatOption={formatDistributionCenterName}
          />
        </FilterField>
        <FilterField label="Orig Arr Wk">
          <SimpleSelect
            value={origArrWk}
            onValueChange={setOrigArrWk}
            options={origArrWkOptions}
          />
        </FilterField>
        <FilterField label="Estimated Arr Wk">
          <SimpleSelect
            value={estimatedArrWk}
            onValueChange={setEstimatedArrWk}
            options={estimatedArrWkOptions}
          />
        </FilterField>
        <FilterField label="PO">
          <SimpleSelect value={po} onValueChange={setPo} options={poOptions} />
        </FilterField>
      </div>

      {rows.length === 0 ? (
        <div className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-white text-sm text-muted-foreground">
          No results for the selected filters.
        </div>
      ) : (
        <POLevelDetailViewTable rows={rows} />
      )}
    </div>
  )
}
